import { memo, useState, useRef, useEffect } from 'react';
import { Handle, Position, NodeProps } from '@xyflow/react';
import { AlertTriangle, Shield, ShieldCheck, ChevronRight, Search, X, User, Bot, Database, Monitor, HardDrive, Server, Key, Globe, Boxes, Layers3 } from 'lucide-react';
import { GraphNodeData, GraphFlowNode, ConnectionChild } from './types';
import { NodeType } from '../../types/graph';
import { cn } from '../../lib/utils';
import githubLogo from '../../assets/github-logo.svg';
import gitlabLogo from '../../assets/gitlab-logo.svg';

const saasLogos: Record<string, string> = {
  github: githubLogo,
  gitlab: gitlabLogo,
};

const borderColors: Record<string, string> = {
  anomaly: '#ef4444',
  role: '#06b6d4',
  user: '#3b82f6',
  service_account: '#8b5cf6',
  resource: '#f59e0b',
  secret: '#f97316',
  token: '#a855f7',
  policy: '#9333ea',
  capability: '#0ea5e9',
  resource_group: '#14b8a6',
  risk: '#dc2626',
};

const getChildIcon = (type: NodeType, resourceType?: string) => {
  if (type === 'role') return Shield;
  if (type === 'resource_group') return Boxes;
  if (type === 'user') return User;
  if (type === 'service_account') return Bot;
  if (type === 'resource') {
    if (resourceType === 'database') return Database;
    if (resourceType === 'virtual-machine') return Monitor;
    if (resourceType === 'storage-bucket') return HardDrive;
    return Server;
  }
  if (type === 'anomaly') return AlertTriangle;
  if (type === 'policy') return ShieldCheck;
  if (type === 'capability') return Layers3;
  if (type === 'risk') return AlertTriangle;
  return Server;
};

export const CircularNode = memo(({ data, selected }: NodeProps<GraphFlowNode>) => {
  const nodeData = data as GraphNodeData;
  const borderColor = borderColors[nodeData.type] || '#6b7280';
  const isAnomaly = nodeData.type === 'anomaly';
  const isPolicy = nodeData.type === 'policy';
  const isRisk = nodeData.type === 'risk';
  const isCapability = nodeData.type === 'capability';
  const isResourceGroup = nodeData.type === 'resource_group';
  const isBlastResource = nodeData.type === 'resource' && nodeData.isBlastRadiusNode;
  const riskSeverity = String(nodeData.severity || '').toUpperCase();
  const riskColor = riskSeverity === 'CRITICAL' ? '#dc2626' : riskSeverity === 'HIGH' ? '#ea580c' : riskSeverity === 'MEDIUM' ? '#d97706' : '#2563eb';
  const bgColor = isAnomaly
    ? '#ef4444'
    : isPolicy
      ? '#9333ea'
      : isRisk
        ? riskColor
        : isCapability
          ? riskColor
          : isResourceGroup
            ? '#14b8a6'
            : isBlastResource
              ? '#f59e0b'
              : 'hsl(var(--background))';
  const iconColor = isAnomaly || isPolicy || isRisk || isCapability || isResourceGroup || isBlastResource ? '#ffffff' : 'hsl(var(--foreground))';

  const [searchQuery, setSearchQuery] = useState('');
  const panelRef = useRef<HTMLDivElement>(null);

  // Connection panel logic
  const isOpen = nodeData.isConnectionPanelOpen;
  const allChildren = nodeData.connectionChildren || [];
  const expandedChildIds = nodeData.expandedChildIds || new Set<string>();

  // For CircularNode (Role/Anomaly), filter out only already expanded children
  // Roles can show resources, Anomalies/Alerts can show resources (Alert → Resource → User → Role hierarchy)
  const children = allChildren.filter(child => !expandedChildIds.has(child.id));
  const totalChildren = children.length;

  // Filter children by search
  const filteredChildren = searchQuery
    ? children.filter(child =>
      child.label?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      child.type?.toLowerCase().includes(searchQuery.toLowerCase())
    )
    : children;

  // Reset search when panel closes
  useEffect(() => {
    if (!isOpen) setSearchQuery('');
  }, [isOpen]);

  const handleChildClick = (childId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    nodeData.onSelectChild?.(childId);
  };

  const blastBadge = (() => {
    if (!nodeData.isBlastRadiusNode) return null;
    if (nodeData.type === 'role') {
      const permissions = Number(nodeData.permissionCount || 0);
      return `${riskSeverity || 'ROLE'}${permissions > 0 ? ` · ${permissions} permissions` : ''}`;
    }
    if (isCapability) {
      const count = Number(nodeData.resourceCount || 0);
      return count > 0 ? `${String(nodeData.accessLevel || 'READ')} · ${count} in scope` : String(nodeData.accessLevel || 'READ');
    }
    if (isResourceGroup) {
      const activeCount = (nodeData.resources as Array<{ activityCount?: number }> | undefined)?.filter((r) => Number(r?.activityCount || 0) > 0).length || 0;
      return activeCount > 0 ? `${activeCount} with recent activity` : 'No recent activity';
    }
    if (isBlastResource) {
      const activity = Number(nodeData.activityCount || 0);
      return activity > 0 ? `${activity} actions` : 'No recent activity';
    }
    return null;
  })();

  const nodeSize = 200;

  return (
    <div
      className="relative flex flex-col items-center cursor-pointer transition-transform duration-200 hover:scale-110"
      style={{ zIndex: !isPolicy && isOpen ? 9999 : 'auto' }}
    >
      {nodeData.hasUnexpandedChildren && !isPolicy && (
        <>
          {/* Third stack (back) */}
          <div
            className="absolute rounded-full border-4"
            style={{
              width: nodeSize,
              height: nodeSize,
              backgroundColor: bgColor,
              borderColor,
              transform: 'translate(24px, 24px)',
              opacity: 0.3,
              boxShadow: `0 4px 12px rgba(0,0,0,0.15)`,
            }}
          />
          {/* Second stack (middle) */}
          <div
            className="absolute rounded-full border-4"
            style={{
              width: nodeSize,
              height: nodeSize,
              backgroundColor: bgColor,
              borderColor,
              transform: 'translate(14px, 14px)',
              opacity: 0.5,
              boxShadow: `0 4px 12px rgba(0,0,0,0.15)`,
            }}
          />
          {/* First stack (front-most behind main) */}
          <div
            className="absolute rounded-full border-4"
            style={{
              width: nodeSize,
              height: nodeSize,
              backgroundColor: bgColor,
              borderColor,
              transform: 'translate(7px, 7px)',
              opacity: 0.7,
              boxShadow: `0 4px 12px rgba(0,0,0,0.15)`,
            }}
          />
        </>
      )}

      {/* Main circle with button wrapper */}
      <div className="relative">
        <div
          className={`rounded-full border-4 flex items-center justify-center transition-all ${selected ? 'ring-4 ring-primary shadow-[0_0_30px_rgba(59,130,246,0.5)]' : ''
            } ${nodeData.isHighlighted ? 'ring-4 ring-primary shadow-[0_0_30px_rgba(59,130,246,0.5)]' : ''}`}
          style={{
            width: nodeSize,
            height: nodeSize,
            backgroundColor: bgColor,
            borderColor,
          }}
        >
          {/* Invisible handles at center for edge connections */}
          <Handle
            type="target"
            position={Position.Left}
            className="!opacity-0 !w-0 !h-0 !border-0"
            style={{ left: '50%', top: '50%' }}
          />
          <Handle
            type="source"
            position={Position.Right}
            className="!opacity-0 !w-0 !h-0 !border-0"
            style={{ right: '50%', top: '50%' }}
          />

          {isAnomaly ? (
            <AlertTriangle className="w-20 h-20" style={{ color: iconColor }} />
          ) : isPolicy ? (
            <ShieldCheck className="w-20 h-20" style={{ color: iconColor }} />
          ) : isRisk ? (
            <AlertTriangle className="w-20 h-20" style={{ color: iconColor }} />
          ) : isCapability ? (
            <Layers3 className="w-20 h-20" style={{ color: iconColor }} />
          ) : isResourceGroup ? (
            <Boxes className="w-20 h-20" style={{ color: iconColor }} />
          ) : isBlastResource ? (
            (() => {
              const ResourceIcon = getChildIcon('resource', nodeData.resourceType);
              return <ResourceIcon className="w-20 h-20" style={{ color: iconColor }} />;
            })()
          ) : nodeData.saasType && saasLogos[(nodeData.saasType || '').toLowerCase()] ? (
            <img
              src={saasLogos[(nodeData.saasType || '').toLowerCase()]}
              alt={nodeData.saasType || ''}
              className={`w-20 h-20 ${(nodeData.saasType || '').toLowerCase() === 'github' ? 'dark:invert' : ''}`}
            />
          ) : nodeData.type === 'secret' ? (
            <Key className="w-20 h-20" style={{ color: iconColor }} />
          ) : nodeData.type === 'token' ? (
            <Globe className="w-20 h-20" style={{ color: iconColor }} />
          ) : (
            <Shield className="w-20 h-20" style={{ color: iconColor }} />
          )}
        </div>

        {/* Crescent moon expand button - hidden in correlations/blast radius mode */}
        {!isPolicy && !nodeData.hideExpandButton && (nodeData.childCount > 0 || (nodeData.connectionChildren && nodeData.connectionChildren.length > 0)) && (
          <div
            className="absolute cursor-pointer"
            style={{
              left: nodeSize + 6,
              top: '50%',
              transform: 'translateY(-50%)',
            }}
            onClick={(e) => {
              e.stopPropagation();
              nodeData.onExpandClick?.(nodeData.nodeId || '');
            }}
          >
            <svg
              width="40"
              height="72"
              viewBox="0 0 28 48"
              className={cn("transition-opacity", isOpen ? "opacity-70" : "hover:opacity-90")}
            >
              {/* Thicker crescent: concave left hugs circle, convex right */}
              <path
                d="M 0 4 
                   Q 14 24, 0 44
                   Q 28 34, 28 24
                   Q 28 14, 0 4
                   Z"
                fill={borderColor}
                filter="drop-shadow(2px 1px 3px rgba(0,0,0,0.15))"
              />
            </svg>
            <div
              className="absolute inset-0 flex items-center justify-center"
              style={{ paddingLeft: 6 }}
            >
              <ChevronRight
                className={cn("w-7 h-7 text-white transition-transform", isOpen && "rotate-90")}
              />
            </div>
          </div>
        )}
      </div>

      {/* Connection dropdown panel */}
      {!isPolicy && isOpen && children.length > 0 && (
        <div
          ref={panelRef}
          className="absolute nodrag nowheel"
          style={{
            top: 0,
            left: nodeSize + 60,
            width: 560,
            zIndex: 9999,
          }}
          onClick={(e) => e.stopPropagation()}
          onWheel={(e) => e.stopPropagation()}
        >
          <div className="rounded-3xl shadow-2xl border-2 bg-card">
            {/* Header with search */}
            <div className="px-6 py-5 border-b bg-muted/50 flex items-center gap-4 rounded-t-3xl">
              <div className="flex-1 flex items-center gap-3 px-4 py-3 rounded-xl bg-background border">
                <Search size={24} className="text-muted-foreground" />
                <input
                  type="text"
                  placeholder={`Search connections...`}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                  className="flex-1 text-xl bg-transparent outline-none text-foreground placeholder:text-muted-foreground"
                />
              </div>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  nodeData.onClosePanel?.();
                }}
                className="p-3 rounded-xl transition-colors hover:bg-muted text-muted-foreground hover:text-foreground"
              >
                <X size={24} />
              </button>
            </div>

            {/* Children list - scrollable dropdown with wheel event capture */}
            <div
              className="overflow-y-auto nowheel"
              style={{ maxHeight: '480px' }}
              onWheel={(e) => e.stopPropagation()}
            >
              {filteredChildren.length === 0 ? (
                <div className="px-6 py-12 text-center text-xl text-muted-foreground">
                  No connections found
                </div>
              ) : (
                filteredChildren.map((child) => {
                  const Icon = getChildIcon(child.type, child.resourceType);
                  const childBorderColor = borderColors[child.type] || '#6b7280';

                  return (
                    <div
                      key={child.id}
                      onClick={(e) => handleChildClick(child.id, e)}
                      className={cn(
                        "px-6 py-6 flex items-center gap-5 cursor-pointer transition-colors border-b last:border-b-0",
                        "hover:bg-muted/50"
                      )}
                      style={{
                        borderLeftWidth: 6,
                        borderLeftColor: childBorderColor,
                      }}
                    >
                      <div
                        className="w-16 h-16 rounded-xl flex items-center justify-center flex-shrink-0"
                        style={{ backgroundColor: `${childBorderColor}15`, border: `2px solid ${childBorderColor}25` }}
                      >
                        <Icon size={32} style={{ color: childBorderColor }} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-2xl font-medium truncate text-foreground">
                          {child.label}
                        </p>
                        <p className="text-lg truncate capitalize text-muted-foreground">
                          {child.type.replace('_', ' ')}
                        </p>
                      </div>
                      {child.hasAnomaly && (
                        <div
                          className="w-12 h-12 rounded-full bg-red-500/20 flex items-center justify-center flex-shrink-0"
                          title="Has anomaly"
                        >
                          <AlertTriangle size={24} className="text-red-500" />
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      )}

      {/* Label below */}
      <span
        className="mt-6 text-3xl font-bold text-foreground text-center max-w-[320px]"
      >
        {nodeData.label}
      </span>
      {nodeData.isBlastRadiusNode && blastBadge && (
        <span className="mt-2 rounded-full border bg-background/90 px-4 py-1 text-base font-semibold text-muted-foreground">
          {blastBadge}
        </span>
      )}
    </div>
  );
});

CircularNode.displayName = 'CircularNode';
